import React, { useState, useEffect, useCallback } from 'react';
import {
  searchAll,
  fetchRankedCandidates,
  fetchRankedEmployers,
  fetchRankedOffers,
  fetchCandidateProfile,
  fetchEmployerProfile,
  fetchOfferDetail,
  type SearchResult,
  type CandidateSortBy,
  type EmployerSortBy,
  type OfferSortBy,
  type CandidateProfileDetail,
  type EmployerProfileDetail,
  type OfferDetailData,
} from '../../lib/engagementService';
import { fetchCampaigns, type SeedCampaign } from '../../lib/seedService';
import { logger } from '../../lib/logger';
import CandidateCard from '../../components/engagement/CandidateCard';
import EmployerCard from '../../components/engagement/EmployerCard';
import OfferCard from '../../components/engagement/OfferCard';

type Tab = 'candidate' | 'employer' | 'offer';

interface HistoryEntry {
  label: string;
  campaign: string;
  count: number;
  date: string;
}

const SORT_OPTIONS: Record<Tab, { value: string; label: string }[]> = {
  candidate: [
    { value: 'likes', label: 'Plus de likes reçus' },
    { value: 'views', label: 'Plus de vues reçues' },
    { value: 'videos', label: 'Plus de vidéos' },
    { value: 'applications', label: 'Plus de candidatures' },
    { value: 'profile_views', label: 'Plus de vues profil' },
    { value: 'least_active', label: 'Moins actifs' },
  ],
  employer: [
    { value: 'offers', label: "Plus d'offres" },
    { value: 'applications_received', label: 'Plus de candidatures reçues' },
    { value: 'selections', label: 'Plus de sélections' },
    { value: 'profile_views_made', label: 'Plus de vues profil' },
  ],
  offer: [
    { value: 'swipes', label: 'Plus de swipes' },
    { value: 'favorites', label: 'Plus de favoris' },
    { value: 'applications', label: 'Plus de candidatures' },
    { value: 'least_active', label: 'Moins actives' },
  ],
};

const DEFAULT_SORT: Record<Tab, string> = {
  candidate: 'likes',
  employer: 'offers',
  offer: 'swipes',
};

const TAB_LABELS: Record<Tab, string> = {
  candidate: 'Candidats',
  employer: 'Recruteurs',
  offer: 'Offres',
};

const EngagementPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>('candidate');
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState(DEFAULT_SORT.candidate);
  const [campaigns, setCampaigns] = useState<SeedCampaign[]>([]);
  const [loading, setLoading] = useState(true);

  const [results, setResults] = useState<SearchResult[]>([]);
  const [resultsLoading, setResultsLoading] = useState(false);

  const [selectedResult, setSelectedResult] = useState<SearchResult | null>(null);
  const [candidateDetail, setCandidateDetail] = useState<CandidateProfileDetail | null>(null);
  const [employerDetail, setEmployerDetail] = useState<EmployerProfileDetail | null>(null);
  const [offerDetailState, setOfferDetailState] = useState<OfferDetailData | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [history, setHistory] = useState<HistoryEntry[]>([]);

  useEffect(() => {
    fetchCampaigns()
      .then((c) => setCampaigns(c.filter((x) => x.status === 'active')))
      .catch((err) => logger.error('load_campaigns', 'EngagementPage', { error: String(err) }))
      .finally(() => setLoading(false));
  }, []);

  const loadResults = useCallback(async () => {
    setResultsLoading(true);
    try {
      if (query.length >= 2) {
        const data = await searchAll(query, tab);
        setResults(data);
      } else if (query.length === 0) {
        if (tab === 'candidate') setResults(await fetchRankedCandidates(sortBy as CandidateSortBy));
        else if (tab === 'employer') setResults(await fetchRankedEmployers(sortBy as EmployerSortBy));
        else setResults(await fetchRankedOffers(sortBy as OfferSortBy));
      } else {
        setResults([]);
      }
    } catch (err) {
      logger.error('load_results', 'EngagementPage', { error: String(err) });
      setResults([]);
    } finally {
      setResultsLoading(false);
    }
  }, [query, tab, sortBy]);

  useEffect(() => {
    if (!loading) loadResults();
  }, [loadResults, loading]);

  const handleTabChange = (newTab: Tab) => {
    setTab(newTab);
    setSortBy(DEFAULT_SORT[newTab]);
    setSelectedResult(null);
    setCandidateDetail(null);
    setEmployerDetail(null);
    setOfferDetailState(null);
  };

  const handleSelect = useCallback(async (result: SearchResult) => {
    setSelectedResult(result);
    setDetailLoading(true);
    setCandidateDetail(null);
    setEmployerDetail(null);
    setOfferDetailState(null);
    try {
      if (result.type === 'candidate') {
        setCandidateDetail(await fetchCandidateProfile(result.userId || result.id));
      } else if (result.type === 'employer') {
        setEmployerDetail(await fetchEmployerProfile(result.userId || result.id));
      } else {
        setOfferDetailState(await fetchOfferDetail(result.id));
      }
    } catch (err) {
      logger.error('load_detail', 'EngagementPage', { error: String(err) });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const refreshDetail = useCallback(async () => {
    if (selectedResult) await handleSelect(selectedResult);
  }, [selectedResult, handleSelect]);

  const pushHistory = (label: string, campaign: string, count: number) => {
    setHistory((prev) => [
      { label, campaign, count, date: new Date().toLocaleString('fr-FR') },
      ...prev,
    ]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  const isRankingMode = query.length === 0;

  return (
    <div className="space-y-6">
      {/* Search & Tabs */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center gap-3 mb-4">
          {(['candidate', 'employer', 'offer'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => handleTabChange(t)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                tab === t
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Rechercher par nom, email, ville${tab === 'offer' ? ', titre' : ''}...`}
            className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
          />
          {isRankingMode && (
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white min-w-[220px]"
            >
              {SORT_OPTIONS[tab].map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          )}
        </div>

        {isRankingMode && (
          <p className="text-xs text-gray-400 mt-2">
            Classement par activité — cliquez sur un profil pour voir le détail et booster
          </p>
        )}
      </div>

      {/* Results List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {resultsLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-6 h-6 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : results.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-gray-400">Aucun résultat</div>
        ) : (
          <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
            {results.map((r) => (
              <button
                key={r.id}
                onClick={() => handleSelect(r)}
                className={`w-full px-5 py-3 flex items-center justify-between text-sm hover:bg-indigo-50 transition-colors ${
                  selectedResult?.id === r.id ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium text-gray-900">{r.name}</span>
                  {r.isSeed && (
                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded">SEED</span>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-gray-400 truncate max-w-[300px]">{r.subtitle}</span>
                  {r.metric !== undefined && (
                    <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-semibold rounded-md min-w-[32px] text-center">
                      {r.metric}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Detail Card */}
      {detailLoading && (
        <div className="flex items-center justify-center py-10">
          <div className="w-6 h-6 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
        </div>
      )}

      {!detailLoading && candidateDetail && (
        <CandidateCard
          detail={candidateDetail}
          campaigns={campaigns}
          onBoostComplete={(label, campaign, count) => {
            pushHistory(label, campaign, count);
            refreshDetail();
          }}
        />
      )}

      {!detailLoading && employerDetail && (
        <EmployerCard
          detail={employerDetail}
          campaigns={campaigns}
          onBoostComplete={(label, campaign, count) => {
            pushHistory(label, campaign, count);
            refreshDetail();
          }}
        />
      )}

      {!detailLoading && offerDetailState && (
        <OfferCard
          detail={offerDetailState}
          campaigns={campaigns}
          onBoostComplete={(label, campaign, count) => {
            pushHistory(label, campaign, count);
            refreshDetail();
          }}
        />
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 text-sm">Historique (session)</h3>
          </div>
          <div className="divide-y divide-gray-50">
            {history.map((h, i) => (
              <div key={i} className="px-5 py-3 flex items-center gap-4 text-sm">
                <span className="w-2 h-2 rounded-full bg-indigo-500" />
                <span className="font-medium text-gray-900">{h.label}</span>
                <span className="text-xs text-gray-500 font-mono">{h.campaign}</span>
                <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-md">
                  {h.count} créées
                </span>
                <span className="text-xs text-gray-400 ml-auto">{h.date}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EngagementPage;
